import { KeyboardEvent, useEffect, useState } from "react";
import { Formik } from "formik";
import { DateTime } from "luxon";
import { Box } from "../../../../../components/Box";
import { Button } from "../../../../../components/Button";
import { Flex } from "../../../../../components/Flex";
import { Paragraph } from "../../../../../components/Paragraph";
import { MIN_AGE_APPLICATION } from "../../../../../utils/constants";
import { personEditValidation } from "../../../../../utils/validationSchema";
import { Input } from "../../../../../components/Input";
import { DatePicker } from "../../../../../components/DatePicker";
import Select, { Option } from "../../../../../components/Select";
import { useCommonTitles } from "../../../../../data/useCommonTitles";
import { useAuth } from "../../../../../context/AuthContext";

export type PersonEditValues = {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  gender: string;
};

export const EditPersonDetailsForm = ({
  initialValues,
  onCancel,
  onSubmit,
}: {
  initialValues: PersonEditValues;
  onCancel: () => void;
  onSubmit: (values: PersonEditValues) => Promise<void> | void;
}) => {
  const { hasPermission } = useAuth();

  const genderOptions: Option[] = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "other", label: "Other" },
  ];

  const { data: titlesData } = useCommonTitles();
  const titleOptions: Option[] =
    titlesData?.data?.map(({ name }: any) => ({ label: name, value: name })) ??
    [];

  const getDatePickerValue = (value: string): Date | string | null => {
    if (!value) return null;

    const isoDate = DateTime.fromISO(value);
    if (isoDate.isValid) {
      return isoDate.toJSDate();
    }

    return value;
  };

  const normalizeDateValue = (value: Date | string | null): string => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return DateTime.fromJSDate(value).toISODate() ?? "";
    }

    if (typeof value === "string") {
      const isoDate = DateTime.fromISO(value);
      if (isoDate.isValid) {
        return isoDate.toISODate() ?? "";
      }

      const formattedDate = DateTime.fromFormat(value, "dd/MM/yyyy");
      if (formattedDate.isValid) {
        return formattedDate.toISODate() ?? "";
      }

      return value;
    }

    return "";
  };

  const [dobValue, setDobValue] = useState<Date | string | null>(() =>
    getDatePickerValue(initialValues.dateOfBirth),
  );
  const [resetDobOnNextKeypress, setResetDobOnNextKeypress] = useState(
    Boolean(initialValues.dateOfBirth),
  );

  useEffect(() => {
    setDobValue(getDatePickerValue(initialValues.dateOfBirth));
    setResetDobOnNextKeypress(Boolean(initialValues.dateOfBirth));
  }, [initialValues.dateOfBirth]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validateOnMount
      validationSchema={personEditValidation}
      onSubmit={async (values, helpers) => {
        try {
          await onSubmit(values);
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => {
        const isSaveDisabled =
          !formik.isValid ||
          formik.isSubmitting ||
          !formik.dirty ||
          !hasPermission("profile.edit");

        const handleDobKeyDownCapture = (
          event: KeyboardEvent<HTMLDivElement>,
        ) => {
          if (!resetDobOnNextKeypress) return;
          if (!/^\d$/.test(event.key)) return;

          setDobValue(null);
          formik.setFieldValue("dateOfBirth", "");
          setResetDobOnNextKeypress(false);
        };

        return (
          <form onSubmit={formik.handleSubmit}>
            <Flex gap="16" mb={3} flexWrap="wrap">
              <Box flex={1} minWidth={200}>
                <Select
                  flex={1}
                  label="Title"
                  name="title"
                  placeholder="Select title"
                  options={titleOptions}
                  value={titleOptions.find((o) => o.value === formik.values.title)}
                  onChange={(opt: any) =>
                    formik.setFieldValue("title", opt?.value ?? "")
                  }
                  hasError={Boolean(formik.touched.title && formik.errors.title)}
                  errorMessage={
                    (formik.touched.title && (formik.errors.title as string)) ||
                    ""
                  }
                />
              </Box>

              <Box flex={1} minWidth={200}>
                <Select
                  label="Gender"
                  name="gender"
                  placeholder="Select gender"
                  options={genderOptions}
                  value={genderOptions.find((o) => o.value === formik.values.gender)}
                  onChange={(opt: any) =>
                    formik.setFieldValue("gender", opt?.value ?? "")
                  }
                  isRequired
                  hasError={Boolean(formik.touched.gender && formik.errors.gender)}
                  errorMessage={
                    (formik.touched.gender && (formik.errors.gender as string)) ||
                    ""
                  }
                />
              </Box>
            </Flex>

            <Flex gap="16" mb={3} flexWrap="wrap">
              <Box flex={1} minWidth={200}>
                <Input
                  label="First name"
                  name="firstName"
                  isRequired
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  hasError={Boolean(
                    formik.touched.firstName && formik.errors.firstName
                  )}
                  errorMessage={
                    (formik.touched.firstName &&
                      (formik.errors.firstName as string)) ||
                    ""
                  }
                />
              </Box>

              <Box flex={1} minWidth={200}>
                <Input
                  label="Middle name(s)"
                  name="middleName"
                  value={formik.values.middleName}
                  onChange={formik.handleChange}
                  hasError={Boolean(
                    formik.touched.middleName && formik.errors.middleName
                  )}
                  errorMessage={
                    (formik.touched.middleName &&
                      (formik.errors.middleName as string)) ||
                    ""
                  }
                />
              </Box>
            </Flex>

            <Flex gap="16" mb={3} flexWrap="wrap">
              <Box flex={1} minWidth={200}>
                <Input
                  label="Last name"
                  name="lastName"
                  isRequired
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  hasError={Boolean(
                    formik.touched.lastName && formik.errors.lastName
                  )}
                  errorMessage={
                    (formik.touched.lastName &&
                      (formik.errors.lastName as string)) ||
                    ""
                  }
                />
              </Box>

              <Box flex={1} minWidth={200}>
                <div onKeyDownCapture={handleDobKeyDownCapture}>
                  <DatePicker
                    label="Date of birth"
                    value={dobValue}
                    format="dd/MM/yyyy"
                    maxDate={DateTime.now()
                      .minus({ years: MIN_AGE_APPLICATION })
                      .toJSDate()}
                    onChange={(d: Date | string | null) => {
                      setDobValue(d);
                      formik.setFieldValue(
                        "dateOfBirth",
                        normalizeDateValue(d),
                      );
                      formik.setFieldTouched("dateOfBirth", true);
                      setResetDobOnNextKeypress(false);
                    }}
                  />
                </div>
                {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
                  <Paragraph mt={1} color="red.60" fontSize={1}>
                    {formik.errors.dateOfBirth as string}
                  </Paragraph>
                )}
              </Box>
            </Flex>

            <Box mb={3}>
              <Input
                label="Email address"
                name="email"
                placeholder="Enter email address"
                value={formik.values.email}
                onChange={formik.handleChange}
                hasError={Boolean(formik.touched.email && formik.errors.email)}
                errorMessage={
                  (formik.touched.email && (formik.errors.email as string)) || ""
                }
              />
            </Box>

            <Flex justifyContent="flex-end" gap="12" mt={4}>
              <Button type="button" variant="text" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                isDisabled={isSaveDisabled}
                title={
                  hasPermission("profile.edit")
                    ? undefined
                    : "You don't have permission to edit this profile"
                }
              >
                {formik.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </Flex>
          </form>
        );
      }}
    </Formik>
  );
};
